import ImgError from '@/shared/assets/images/img-error.svg?react';
import ImgUpdate from '@/shared/assets/images/img-update.svg?react';
import { ServicePage } from '@/widgets/service-page';
import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

const ErrorFallback: React.FC<FallbackProps> = ({
  error,
  resetErrorBoundary,
}) => {
  const { t } = useTranslation();
  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  const errorMessage =
    error instanceof Error ? error.message : 'An unexpected error occurred!';

  const isMissedJsFileError = errorMessage.includes(
    'Failed to fetch dynamically imported module',
  );

  const computedErrorMessage = isMissedJsFileError
    ? t('Чтобы всё работало корректно, пожалуйста, перезагрузите страницу')
    : errorMessage;

  return (
    <>
      <ServicePage
        title={
          isMissedJsFileError
            ? t('Обновление приложения')
            : t('Упс! Что-то пошло не так')
        }
        description={computedErrorMessage}
        image={isMissedJsFileError ? <ImgUpdate /> : <ImgError />}
        wrapperClassName="h-screen w-screen"
        buttonText={t('Перезагрузить')}
        onReset={resetErrorBoundary}
      />
    </>
  );
};

type ErrorBoundaryWrapperProps = {
  children: React.ReactNode;
  onReset?: () => void; // Optional reset logic
};

export const ErrorBoundaryWrapper: React.FC<ErrorBoundaryWrapperProps> = ({
  children,
  onReset,
}) => {
  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={(error, info) => {
        // Additional error logging or handling
        console.error('ErrorBoundary caught an error:', error, info);
        Sentry.captureException(error, {
          extra: { componentStack: info.componentStack },
        });
      }}
      onReset={onReset}
    >
      {children}
    </ErrorBoundary>
  );
};
